/**
 * 动画播放器模块
 * 用于解析和播放二进制格式的动画数据
 */
class AnimationPlayer {
  /**
     * 创建动画播放器实例
     * @param {Object} options 配置选项
     * @param {HTMLCanvasElement} options.canvas 用于渲染动画的Canvas元素
     * @param {number} options.width Canvas宽度（可选，默认使用canvas的宽度）
     * @param {number} options.height Canvas高度（可选，默认使用canvas的高度）
     */
  constructor (options) {
    this.canvas = options.canvas
    this.ctx = this.canvas.getContext('2d')
    this.width = options.width || this.canvas.width
    this.height = options.height || this.canvas.height

    // 动画数据
    this.animationData = null
    this.frames = []
    this.shapes = []

    // 播放控制
    this.currentFrameIndex = 0
    this.isPlaying = false
    this.isLooping = false
    this.lastFrameTime = 0
    this.animationTimer = null

    // 事件回调
    this.onFrameChange = null
    this.onAnimationEnd = null

    // 初始化画布
    this.canvas.width = this.width
    this.canvas.height = this.height
  }

  /**
     * 从十六进制字符串加载动画
     * @param {string} hexString 十六进制字符串
     * @returns {boolean} 是否成功加载
     */
  loadFromHexString (hexString) {
    try {
      // 将十六进制字符串转换为Uint8Array
      const bytes = new Uint8Array(
        hexString.match(/.{1,2}/g).map(byte => parseInt(byte, 16))
      )
      return this.loadFromBinary(bytes)
    } catch (error) {
      console.error('加载十六进制动画数据失败:', error)
      return false
    }
  }

  /**
     * 从二进制数据加载动画
     * @param {Uint8Array} binaryData 二进制数据
     * @returns {boolean} 是否成功加载
     */
  loadFromBinary (binaryData) {
    try {
      // 清理之前的GIF覆盖层
      this.clearGifOverlay()
      // 解析二进制数据
      const animData = this._parseBinaryData(binaryData)
      if (!animData) return false

      // 设置动画数据
      this.animationData = animData

      this.frames = animData.frames
      this.shapes = animData.shapes
      // this.isLooping = animData.loop;

      // 重置播放状态
      this.currentFrameIndex = 0
      this.isPlaying = false

      // 绘制第一帧
      this._renderFrame(0)

      return true
    } catch (error) {
      console.error('加载二进制动画数据失败:', error)
      return false
    }
  }

  /**
     * 从URL加载动画
     * @param {string} url 动画数据的URL地址
     * @param {Object} options 请求选项（可选）
     * @returns {Promise<boolean>} 表示加载是否成功的Promise
     */
  // 解析GIF文件获取播放时长
  async _parseGifDuration (blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        try {
          const arrayBuffer = reader.result
          const bytes = new Uint8Array(arrayBuffer)

          // 检查GIF文件头
          const header = new TextDecoder().decode(bytes.slice(0, 6))
          if (header !== 'GIF87a' && header !== 'GIF89a') {
            throw new Error('不是有效的GIF文件')
          }

          let totalDuration = 0
          let frameCount = 0
          let offset = 13 // 跳过文件头和逻辑屏幕描述符

          // 解析数据流
          while (offset < bytes.length) {
            const separator = bytes[offset]

            if (separator === 0x21) { // Extension
              const label = bytes[offset + 1]
              if (label === 0xF9) { // Graphic Control Extension
                // 读取延迟时间 (2字节，小端序，单位为1/100秒)
                const delay = bytes[offset + 4] | (bytes[offset + 5] << 8)
                totalDuration += delay * 10 // 转换为毫秒
                frameCount++
                offset += 8 // 跳过图形控制扩展
              } else {
                // 跳过其他扩展
                offset += 2
                while (offset < bytes.length) {
                  const blockSize = bytes[offset++]
                  if (blockSize === 0) break
                  offset += blockSize
                }
              }
            } else if (separator === 0x2C) { // Image
              // 跳过图像描述符
              offset += 10
              // 跳过局部颜色表（如果存在）
              const packed = bytes[offset - 1]
              if (packed & 0x80) {
                const colorTableSize = 1 << ((packed & 0x07) + 1)
                offset += colorTableSize * 3
              }
              // 跳过LZW最小代码长度
              offset++
              // 跳过图像数据
              while (offset < bytes.length) {
                const blockSize = bytes[offset++]
                if (blockSize === 0) break
                offset += blockSize
              }
            } else if (separator === 0x3B) { // Trailer
              break
            } else {
              offset++
            }
          }

          // 如果没有找到帧或延迟时间为0，使用默认值
          if (totalDuration === 0) {
            totalDuration = frameCount > 0 ? frameCount * 100 : 3000 // 默认每帧100ms或3秒
          }

          console.log(`解析GIF: ${frameCount}帧, 总时长: ${totalDuration}ms`)
          resolve(totalDuration)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = () => reject(new Error('读取文件失败'))
      reader.readAsArrayBuffer(blob)
    })
  }

  // 清理GIF覆盖层
  clearGifOverlay () {
    console.log('[GIF清理] 开始清理GIF覆盖层')

    // 清理当前记录的覆盖层
    if (this.currentGifOverlay) {
      console.log('[GIF清理] 移除当前GIF覆盖层')
      this.currentGifOverlay.remove()
      this.currentGifOverlay = null
    }

    // 强制清理所有可能的GIF覆盖层
    if (this.canvas?.parentElement) {
      const overlays = this.canvas.parentElement.querySelectorAll('.gif-overlay')
      console.log('[GIF清理] 找到', overlays.length, '个GIF覆盖层')
      overlays.forEach((overlay, index) => {
        console.log('[GIF清理] 移除覆盖层', index)
        overlay.remove()
      })
    }

    // 也清理整个文档中可能遗留的覆盖层（防御性清理）
    document.querySelectorAll('.gif-overlay').forEach((overlay, index) => {
      console.log('[GIF清理] 清理文档中遗留的覆盖层', index)
      overlay.remove()
    })

    // 清理GIF相关状态
    this.currentGifDuration = null
    if (this.gifEndTimer) {
      clearTimeout(this.gifEndTimer)
      this.gifEndTimer = null
    }

    console.log('[GIF清理] GIF覆盖层清理完成')
  }

  loadFromUrl (url, options = {}) {
    return new Promise(async (resolve, reject) => {
      try {
        // 清理之前的GIF覆盖层
        this.clearGifOverlay()
        // 发起网络请求获取数据
        console.log('Fetch 开始加载：', url)
        const response = await fetch(url, options)
        console.log('Fetch 已完成加载：', response.url)

        if (!response.ok) {
          throw new Error(`网络请求失败: ${response.status} ${response.statusText}`)
        }

        // 判断URL是否是hex文件
        if (url.toLowerCase().endsWith('.hex')) {
          // 获取文本数据作为hex字符串
          const hexText = await response.text()
          console.log('接收到hex文本数据，长度:', hexText.length, '前50个字符:', hexText.substring(0, 50))

          // 使用loadFromHexString处理hex文本
          const result = this.loadFromHexString(hexText)
          if (this.setAnimationDataVersion) {
            this.setAnimationDataVersion(v => v + 1)
          }
          resolve(result)
        } else {
          // 检查是否是GIF文件
          if (url.toLowerCase().endsWith('.gif') || response.headers.get('content-type')?.includes('image/gif')) {
            console.log('检测到GIF文件，直接渲染到画布')

            // 获取blob数据
            const blob = await response.blob()
            const imageUrl = URL.createObjectURL(blob)

            // 解析GIF获取播放时长
            try {
              this.currentGifDuration = await this._parseGifDuration(blob)
              console.log(`[GIF解析] 成功解析播放时长: ${this.currentGifDuration}ms`)
            } catch (err) {
              console.warn('[GIF解析] 解析GIF时长失败，使用默认值:', err)
              this.currentGifDuration = 3000 // 默认3秒
            }

            // 创建Image对象并加载GIF
            const img = new Image()
            img.onload = () => {
              if (this.canvas) {
                // 清空画布并填充黑色背景
                if (this.ctx) {
                  this.ctx.fillStyle = '#000000'
                  this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
                }

                // 创建GIF覆盖层
                const gifOverlay = document.createElement('img')
                gifOverlay.className = 'gif-overlay'
                gifOverlay.src = imageUrl
                gifOverlay.setAttribute('data-gif-url', url) // 添加URL标识

                // 获取canvas的位置和尺寸
                const canvasRect = this.canvas.getBoundingClientRect()
                const canvasStyle = window.getComputedStyle(this.canvas)

                // 计算缩放比例以适应画布
                const scaleX = this.canvas.width / img.width
                const scaleY = this.canvas.height / img.height
                const scale = Math.min(scaleX, scaleY)

                // 计算实际显示尺寸
                const scaledWidth = img.width * scale
                const scaledHeight = img.height * scale

                // 设置覆盖层样式
                gifOverlay.style.position = 'absolute'
                gifOverlay.style.left = '50%'
                gifOverlay.style.top = '50%'
                gifOverlay.style.transform = 'translate(-50%, -50%)'
                gifOverlay.style.width = `${scaledWidth}px`
                gifOverlay.style.height = `${scaledHeight}px`
                gifOverlay.style.maxWidth = `${this.canvas.width}px`
                gifOverlay.style.maxHeight = `${this.canvas.height}px`
                gifOverlay.style.objectFit = 'contain'
                gifOverlay.style.pointerEvents = 'none' // 不影响canvas交互
                gifOverlay.style.zIndex = '10'

                // 确保canvas父容器有相对定位
                if (this.canvas.parentElement) {
                  const parentStyle = window.getComputedStyle(this.canvas.parentElement)
                  if (parentStyle.position === 'static') {
                    this.canvas.parentElement.style.position = 'relative'
                  }

                  // 添加GIF覆盖层到canvas父容器
                  this.canvas.parentElement.appendChild(gifOverlay)
                }

                console.log(`GIF覆盖层已创建: ${img.width}x${img.height} -> ${scaledWidth}x${scaledHeight}`)

                // 存储覆盖层引用，用于后续清理
                this.currentGifOverlay = gifOverlay
              }

              // 设置简单的动画数据结构用于兼容
              const gifAnimationData = {
                name: 'GIF动画',
                description: '从GIF文件加载',
                loop: true,
                backgroundColor: 0x000000,
                width: this.canvas?.width || 240,
                height: this.canvas?.height || 220,
                frames: [
                  {
                    duration: 100,
                    shapes: {}
                  }
                ]
              }

              if (this.setAnimationDataVersion) {
                this.setAnimationDataVersion(v => v + 1)
              }

              resolve(gifAnimationData)
            }

            img.onerror = () => {
              URL.revokeObjectURL(imageUrl)
              reject(new Error('GIF图像加载失败'))
            }

            // 延迟清理URL，确保GIF有时间开始播放
            setTimeout(() => {
              URL.revokeObjectURL(imageUrl)
            }, 1000)

            img.src = imageUrl
          } else {
            // 获取二进制数据
            const arrayBuffer = await response.arrayBuffer()
            const binaryData = new Uint8Array(arrayBuffer)
            console.log('接收到二进制数据，长度:', binaryData.length)

            // 使用二进制数据加载动画
            const result = this.loadFromBinary(binaryData)
            if (this.setAnimationDataVersion) {
              this.setAnimationDataVersion(v => v + 1)
            }
            resolve(result)
          }
        }
      } catch (error) {
        console.error('从URL加载动画失败:', error)
        reject(error)
        return false
      }
    })
  }

  /**
     * 解析二进制数据
     * @private
     * @param {Uint8Array} data 二进制数据
     * @returns {Object|null} 解析后的动画数据或null
     */
  _parseBinaryData (data) {
    let offset = 0
    const result = {}

    try {
      // 1. 读取名称
      const nameLength = data[offset++]
      const nameBytes = data.slice(offset, offset + nameLength)
      result.name = new TextDecoder().decode(nameBytes)
      offset += nameLength

      // 2. 读取描述
      const descLength = data[offset++]
      const descBytes = data.slice(offset, offset + descLength)
      result.description = new TextDecoder().decode(descBytes)
      offset += descLength

      // 3. 读取基本属性
      // 宽度（2字节）
      result.width = (data[offset] << 8) | data[offset + 1]
      offset += 2
      // 高度（2字节）
      result.height = (data[offset] << 8) | data[offset + 1]
      offset += 2
      // 循环标志（1字节）
      result.loop = data[offset++] === 1
      // 背景颜色（3字节 RGB）
      const r = data[offset++]
      const g = data[offset++]
      const b = data[offset++]
      result.backgroundColor = (r << 16) | (g << 8) | b

      // 4. 读取帧数（2字节）
      const frameCount = (data[offset] << 8) | data[offset + 1]
      offset += 2

      // 5. 读取所有帧的持续时间
      const frameDurations = []
      for (let i = 0; i < frameCount; i++) {
        const duration = (data[offset] << 8) | data[offset + 1]
        frameDurations.push(duration)
        offset += 2
      }

      // 6. 读取形状总数（2字节）
      const shapeCount = (data[offset] << 8) | data[offset + 1]
      offset += 2

      // 7. 读取所有形状数据
      const shapes = []
      for (let i = 0; i < shapeCount; i++) {
        const shape = {}

        // 形状类型（1字节）
        const typeValue = data[offset++]
        switch (typeValue) {
          case 0: shape.type = 'rectangle'; break
          case 1: shape.type = 'circle'; break
          case 2: shape.type = 'triangle'; break
          case 3: shape.type = 'polygon'; break
          default: shape.type = 'rectangle'
        }

        // 基本属性
        shape.width = data[offset++] // 宽度（1字节）
        shape.height = data[offset++] // 高度（1字节）

        // 位置（每个坐标2字节）
        shape.pos_x = (data[offset] << 8) | data[offset + 1]
        offset += 2
        shape.pos_y = (data[offset] << 8) | data[offset + 1]
        offset += 2

        // 旋转（2字节，有符号整数）
        let rotation = (data[offset] << 8) | data[offset + 1]
        // 处理有符号整数
        if (rotation & 0x8000) {
          rotation = rotation - 0x10000
        }
        shape.rotation = rotation
        offset += 2

        shape.radius = data[offset++] // 圆角半径（1字节）
        shape.opacity = data[offset++] // 不透明度（1字节）

        // 颜色（3字节 RGB）
        const shapeR = data[offset++]
        const shapeG = data[offset++]
        const shapeB = data[offset++]
        shape.color = (shapeR << 16) | (shapeG << 8) | shapeB

        // 对齐方式（1字节）
        const alignValue = data[offset++]
        switch (alignValue) {
          case 0: shape.align = 'CENTER'; break
          case 1: shape.align = 'LEFT'; break
          case 2: shape.align = 'RIGHT'; break
          case 3: shape.align = 'TOP'; break
          case 4: shape.align = 'BOTTOM'; break
          default: shape.align = 'CENTER'
        }

        // 顶点数据
        const vertexCount = data[offset++]
        shape.vertices = []
        for (let j = 0; j < vertexCount; j++) {
          // X坐标（2字节，有符号）
          let x = (data[offset] << 8) | data[offset + 1]
          if (x & 0x8000) x = x - 0x10000
          offset += 2

          // Y坐标（2字节，有符号）
          let y = (data[offset] << 8) | data[offset + 1]
          if (y & 0x8000) y = y - 0x10000
          offset += 2

          shape.vertices.push({ x, y })
        }

        shapes.push(shape)
      }

      // 8. 读取每一帧的形状引用
      const frames = []
      for (let i = 0; i < frameCount; i++) {
        const frame = {
          duration: frameDurations[i],
          shapes: {}
        }

        // 读取当前帧的形状数量（1字节）
        const frameShapeCount = data[offset++]

        // 读取每个形状的索引
        for (let j = 0; j < frameShapeCount; j++) {
          const shapeIndex = data[offset++]
          // 使用唯一ID作为键
          frame.shapes['shape_' + j] = shapes[shapeIndex]
        }

        frames.push(frame)
      }

      result.frames = frames
      result.shapes = shapes

      return result
    } catch (error) {
      console.error('解析二进制数据失败:', error, '偏移量:', offset)
      return null
    }
  }

  /**
     * 开始播放动画
     */
  play () {
    // 对于GIF文件，不需要animationData，只要有GIF覆盖层就可以播放
    if (this.isPlaying) return
    if (!this.currentGifOverlay && !this.animationData) return

    this.isPlaying = true

    // 如果是GIF，设置定时器触发结束回调
    if (this.currentGifOverlay && this.currentGifDuration) {
      console.log(`[GIF播放] 开始播放GIF，将在${this.currentGifDuration}ms后触发结束回调`)
      console.log(`[GIF播放] 当前回调函数:`, typeof this.onAnimationEnd)
      console.log(`[GIF播放] 播放状态:`, this.isPlaying)
      this.gifEndTimer = setTimeout(() => {
        console.log('[GIF播放] GIF播放完成，触发结束回调')
        console.log('[GIF播放] 回调函数存在:', !!this.onAnimationEnd)
        console.log('[GIF播放] 当前播放状态:', this.isPlaying)
        if (this.onAnimationEnd) {
          this.onAnimationEnd()
        } else {
          console.warn('[GIF播放] 没有设置结束回调函数')
        }
      }, this.currentGifDuration)
    } else {
      // 传统动画播放
      this.lastFrameTime = performance.now()
      this._nextFrame()
    }
  }

  /**
     * 暂停动画播放
     */
  pause () {
    this.isPlaying = false
    if (this.animationTimer) {
      cancelAnimationFrame(this.animationTimer)
      this.animationTimer = null
    }
    // 清理GIF结束定时器
    if (this.gifEndTimer) {
      clearTimeout(this.gifEndTimer)
      this.gifEndTimer = null
    }
  }

  /**
     * 停止动画播放并重置到第一帧
     */
  stop () {
    this.pause()
    this.currentFrameIndex = 0
    if (this.animationData) {
      this._renderFrame(0)
    }
  }

  /**
     * 跳转到指定帧
     * @param {number} frameIndex 帧索引
     */
  goToFrame (frameIndex) {
    if (!this.animationData) return

    const wasPlaying = this.isPlaying
    this.pause()

    // 确保帧索引在有效范围内
    this.currentFrameIndex = Math.max(0, Math.min(frameIndex, this.frames.length - 1))
    this._renderFrame(this.currentFrameIndex)

    // 如果之前在播放，则继续播放
    if (wasPlaying) {
      this.play()
    }
  }

  /**
     * 设置是否循环播放
     * @param {boolean} loop 是否循环
     */
  setLoop (loop) {
    this.isLooping = loop
  }

  /**
     * 设置帧变化回调函数
     * @param {Function} callback 回调函数，参数为当前帧索引
     */
  onFrameChanged (callback) {
    this.onFrameChange = callback
  }

  /**
     * 设置动画结束回调函数
     * @param {Function} callback 回调函数
     */
  onEnded (callback) {
    console.log('[回调设置] 设置结束回调:', typeof callback, callback ? '存在' : '清空')
    this.onAnimationEnd = callback
  }

  /**
     * 处理下一帧动画
     * @private
     */
  _nextFrame () {
    if (!this.isPlaying) return

    const now = performance.now()
    const currentFrame = this.frames[this.currentFrameIndex]
    const elapsed = now - this.lastFrameTime

    // 如果已经过了当前帧的持续时间，则进入下一帧
    if (elapsed >= currentFrame.duration) {
      this.lastFrameTime = now
      this.currentFrameIndex++

      // 每100帧打印一次日志
      if (this.currentFrameIndex % 100 === 0) {
        console.log(`已播放到第 ${this.currentFrameIndex} 帧`)
      }

      // 检查是否到达动画末尾
      if (this.currentFrameIndex >= this.frames.length) {
        console.log('到达动画末尾')
        if (this.isLooping) {
          // 循环播放
          console.log('循环播放，重置帧索引为0')
          this.currentFrameIndex = 0
        } else {
          // 停止播放
          console.log('非循环模式，停止播放')
          this.isPlaying = false
          this.currentFrameIndex = this.frames.length - 1

          // 触发动画结束回调
          if (this.onAnimationEnd) {
            this.onAnimationEnd()
          }

          return
        }
      }

      // 渲染当前帧
      this._renderFrame(this.currentFrameIndex)

      // 触发帧变化回调
      if (this.onFrameChange) {
        this.onFrameChange(this.currentFrameIndex)
      }
    }

    // 请求下一帧动画
    this.animationTimer = requestAnimationFrame(() => this._nextFrame())
  }

  /**
     * 渲染指定帧
     * @private
     * @param {number} frameIndex 帧索引
     */
  _renderFrame (frameIndex) {
    if (!this.animationData || frameIndex >= this.frames.length) return

    const frame = this.frames[frameIndex]
    const ctx = this.ctx

    // 清除画布
    ctx.clearRect(0, 0, this.width, this.height)
    // 绘制背景色
    if (this.animationData.backgroundColor) {
      const bgColor = this.animationData.backgroundColor
      const r = (bgColor >> 16) & 0xFF
      const g = (bgColor >> 8) & 0xFF
      const b = bgColor & 0xFF
      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
      ctx.fillRect(0, 0, this.width, this.height)
    } else {
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, this.width, this.height)
    }

    // 计算中心点坐标（用于坐标转换）
    const centerX = this.width / 2
    const centerY = this.height / 2

    // 绘制所有形状
    if (frame.shapes.length > 0) {
      console.error('frame.shapes.length > 0')
    }
    for (const shapeId in frame.shapes) {
      const shape = frame.shapes[shapeId]
      // console.log(`绘制形状 ${shapeId}:`, shape);
      this._drawShape(shape, centerX, centerY)
    }
  }

  /**
     * 绘制形状
     * @private
     * @param {Object} shape 形状数据
     * @param {number} centerX 画布中心X坐标
     * @param {number} centerY 画布中心Y坐标
     */
  _drawShape (shape, centerX, centerY) {
    const ctx = this.ctx
    const width = shape.width || 0
    const height = shape.height || 0

    // 设置颜色和透明度
    const color = shape.color || 0
    const r = (color >> 16) & 0xFF
    const g = (color >> 8) & 0xFF
    const b = color & 0xFF
    const opacity = (shape.opacity !== undefined ? shape.opacity : 255) / 255

    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
    ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`
    ctx.globalAlpha = opacity

    // 保存当前上下文状态
    ctx.save()

    // 从LVGL坐标系（左上角为原点）转换为Canvas坐标系（中心为原点）
    // 注意：shape.pos_x和shape.pos_y是LVGL中的左上角坐标
    let canvasX, canvasY

    if (shape.type === 'polygon' || shape.type === 'triangle') {
      // 多边形和三角形使用原始坐标
      canvasX = shape.pos_x - centerX
      canvasY = shape.pos_y - centerY
    } else if (shape.type === 'circle') {
      // 圆形使用中心点坐标
      canvasX = shape.pos_x + Math.floor(width / 2) - centerX
      canvasY = shape.pos_y + Math.floor(width / 2) - centerY
    } else {
      // 矩形使用中心点坐标
      canvasX = shape.pos_x + Math.floor(width / 2) - centerX
      canvasY = shape.pos_y + Math.floor(height / 2) - centerY
    }

    // 应用旋转变换
    if (shape.rotation) {
      // 移动到形状中心点
      ctx.translate(centerX + canvasX, centerY + canvasY)
      // 旋转（角度转弧度）
      ctx.rotate((shape.rotation * Math.PI) / 180)
      // 移回原点
      ctx.translate(-(centerX + canvasX), -(centerY + canvasY))
    }

    // 根据形状类型绘制
    switch (shape.type) {
      case 'rectangle':
        if (shape.radius > 0) {
          // 绘制圆角矩形
          this._drawRoundedRect(
            centerX + canvasX - Math.floor(width / 2),
            centerY + canvasY - Math.floor(height / 2),
            width,
            height,
            shape.radius
          )
        } else {
          // 绘制普通矩形
          ctx.fillRect(
            centerX + canvasX - Math.floor(width / 2),
            centerY + canvasY - Math.floor(height / 2),
            width,
            height
          )
        }
        break

      case 'circle':
        ctx.beginPath()
        ctx.arc(
          centerX + canvasX,
          centerY + canvasY,
          width / 2, // 使用宽度作为直径
          0,
          Math.PI * 2
        )
        ctx.fill()
        break

      case 'triangle':
        // 绘制三角形
        ctx.beginPath()
        // 使用默认三角形顶点（等边三角形）
        const triangleHeight = (Math.sqrt(3) / 2) * width
        ctx.moveTo(centerX + canvasX, centerY + canvasY - triangleHeight / 2)
        ctx.lineTo(centerX + canvasX - width / 2, centerY + canvasY + triangleHeight / 2)
        ctx.lineTo(centerX + canvasX + width / 2, centerY + canvasY + triangleHeight / 2)
        ctx.closePath()
        ctx.fill()
        break

      case 'polygon':
        // 绘制多边形
        if (Array.isArray(shape.vertices) && shape.vertices.length > 0) {
          ctx.beginPath()
          // 直接使用顶点坐标，与设计器保持一致
          const firstVertex = shape.vertices[0]
          ctx.moveTo(
            firstVertex.x,
            firstVertex.y
          )

          for (let i = 1; i < shape.vertices.length; i++) {
            const vertex = shape.vertices[i]
            ctx.lineTo(
              vertex.x,
              vertex.y
            )
          }

          ctx.closePath()
          ctx.fill()
        }
        break
    }

    // 恢复上下文状态
    ctx.restore()
  }

  /**
     * 绘制圆角矩形
     * @private
     * @param {number} x 左上角X坐标
     * @param {number} y 左上角Y坐标
     * @param {number} width 宽度
     * @param {number} height 高度
     * @param {number} radius 圆角半径
     */
  _drawRoundedRect (x, y, width, height, radius) {
    const ctx = this.ctx
    radius = Math.min(radius, Math.min(width / 2, height / 2))

    ctx.beginPath()
    ctx.moveTo(x + radius, y)
    ctx.lineTo(x + width - radius, y)
    ctx.arcTo(x + width, y, x + width, y + radius, radius)
    ctx.lineTo(x + width, y + height - radius)
    ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius)
    ctx.lineTo(x + radius, y + height)
    ctx.arcTo(x, y + height, x, y + height - radius, radius)
    ctx.lineTo(x, y + radius)
    ctx.arcTo(x, y, x + radius, y, radius)
    ctx.closePath()
    ctx.fill()
  }

  /**
     * 获取当前帧索引
     * @returns {number} 当前帧索引
     */
  getCurrentFrame () {
    return this.currentFrameIndex
  }

  /**
     * 获取总帧数
     * @returns {number} 总帧数
     */
  getTotalFrames () {
    return this.frames ? this.frames.length : 0
  }

  /**
     * 获取当前帧持续时间
     * @returns {number} 当前帧持续时间（毫秒）
     */
  getCurrentFrameDuration () {
    if (!this.frames || this.currentFrameIndex >= this.frames.length) return 0
    return this.frames[this.currentFrameIndex].duration
  }

  /**
     * 获取动画总时长
     * @returns {number} 动画总时长（毫秒）
     */
  getTotalDuration () {
    if (!this.frames) return 0
    return this.frames.reduce((total, frame) => total + frame.duration, 0)
  }

  /**
     * 获取动画名称
     * @returns {string} 动画名称
     */
  getName () {
    return this.animationData ? this.animationData.name : ''
  }

  /**
     * 获取动画描述
     * @returns {string} 动画描述
     */
  getDescription () {
    return this.animationData ? this.animationData.description : ''
  }

  /**
     * 获取动画宽度
     * @returns {number} 动画宽度
     */
  getWidth () {
    return this.animationData ? this.animationData.width : this.width
  }

  /**
     * 获取动画高度
     * @returns {number} 动画高度
     */
  getHeight () {
    return this.animationData ? this.animationData.height : this.height
  }

  /**
     * 获取是否正在播放
     * @returns {boolean} 是否正在播放
     */
  isPlayingAnimation () {
    return this.isPlaying
  }

  /**
     * 获取是否循环播放
     * @returns {boolean} 是否循环播放
     */
  isLoopingAnimation () {
    return this.isLooping
  }

  /**
     * 调整播放器大小
     * @param {number} width 新宽度
     * @param {number} height 新高度
     */
  resize (width, height) {
    this.width = width
    this.height = height
    this.canvas.width = width
    this.canvas.height = height

    // 重新渲染当前帧
    if (this.animationData) {
      this._renderFrame(this.currentFrameIndex)
    }
  }

  /**
     * 销毁播放器实例
     */
  destroy () {
    this.pause()
    this.animationData = null
    this.frames = []
    this.shapes = []
    this.onFrameChange = null
    this.onAnimationEnd = null
    this.ctx.clearRect(0, 0, this.width, this.height)
  }

  /**
     * 从流程数据加载动画流程
     * @param {Object} flow {nodes, edges, globalVars}
     * @param {Object} options {onNodeChange, onStatusChange}
     */
  loadFromFlow (flow, options = {}) {
    this.flowData = flow
    this.flowOptions = options
    this.flowIsPlaying = false
    this.flowCurrentNodeId = null
    this.flowLoopState = {} // 记录每个循环节点的计数
    this.flowTimeout = null
    if (this.flowOptions.onStatusChange) {
      this.flowOptions.onStatusChange('ready')
    }
  }

  /**
     * 开始流程模拟播放
     */
  playFlow () {
    if (!this.flowData || !this.flowData.nodes || !this.flowData.edges) return
    this.flowIsPlaying = true
    if (this.flowOptions && this.flowOptions.onStatusChange) {
      this.flowOptions.onStatusChange('playing')
    }
    this.flowCurrentNodeId = null
    this.flowLoopState = {}
    this._playNextFlowNode()
  }

  /**
     * 停止流程模拟播放
     */
  stopFlow () {
    this.flowIsPlaying = false
    if (this.flowTimeout) {
      clearTimeout(this.flowTimeout)
      this.flowTimeout = null
    }
    if (this.flowOptions && this.flowOptions.onStatusChange) {
      this.flowOptions.onStatusChange('stopped')
    }
    this.flowCurrentNodeId = null
    this.flowLoopState = {}
  }

  /**
     * 内部方法：播放下一个流程节点
     */
  _playNextFlowNode () {
    console.log('[流程] _playNextFlowNode, 当前节点:', this.flowCurrentNodeId, 'isPlaying:', this.flowIsPlaying)
    if (!this.flowIsPlaying) return
    const nodes = this.flowData.nodes
    const edges = this.flowData.edges
    // 如果没有当前节点，从start节点开始
    if (!this.flowCurrentNodeId) {
      const startNode = nodes.find(n => n.type === 'start')
      console.log('[流程] _playNextFlowNode, 没有当前节点，查找start节点:', startNode)
      if (startNode) {
        this.flowCurrentNodeId = startNode.id
        // 直接进入下一个节点
        const nextId = this._findNextFlowNode(startNode.id)
        console.log('[流程] start节点的下一个节点:', nextId)
        if (nextId) {
          this.flowCurrentNodeId = nextId
          this._playFlowNode(nextId)
        } else {
          console.warn('[流程] start节点没有下一个节点，流程结束')
          this._endFlowSimulation()
        }
      } else {
        console.warn('[流程] 没有start节点，流程结束')
        this._endFlowSimulation()
      }
      return
    }
    // 获取当前节点
    const node = nodes.find(n => n.id === this.flowCurrentNodeId)
    console.log('[流程] _playNextFlowNode, 当前节点对象:', node)
    if (!node) {
      console.warn('[流程] 当前节点对象不存在，流程结束')
      this._endFlowSimulation()
      return
    }
    // 查找下一个节点
    const nextId = this._findNextFlowNode(node.id)
    console.log('[流程] _playNextFlowNode, 下一个节点:', nextId)
    if (nextId) {
      this.flowCurrentNodeId = nextId
      this._playFlowNode(nextId)
    } else {
      console.warn('[流程] 没有下一个节点，流程结束')
      this._endFlowSimulation()
    }
  }

  /**
     * 内部方法：播放指定流程节点
     */
  _playFlowNode (nodeId) {
    console.log('[流程] _playFlowNode, nodeId:', nodeId)
    if (!this.flowIsPlaying) return
    const nodes = this.flowData.nodes
    const edges = this.flowData.edges
    const node = nodes.find(n => n.id === nodeId)
    console.log('[流程] _playFlowNode, node:', node)
    if (!node) {
      console.warn('[流程] _playFlowNode, node不存在，流程结束')
      this._endFlowSimulation()
      return
    }
    // 回调当前节点
    if (this.flowOptions && this.flowOptions.onNodeChange) {
      console.log('[流程] onNodeChange回调:', node)
      this.flowOptions.onNodeChange(node)
    }
    // 根据类型处理
    switch (node.type) {
      case 'action': {
        // 动作节点，加载动画并播放
        const animId = node.data && node.data.id
        console.log('[流程] 进入action节点:', node, 'animId:', animId)
        if (animId) {
          // 防止重复加载同一个动画
          const animUrl = `https://oss.goticket.cn/bot_anima/${animId}.gif`
          console.log('[流程] action节点加载动画URL:', animUrl)

          // 如果当前已经在播放，先停止
          if (this.isPlaying) {
            console.log('[流程] 检测到正在播放，先停止当前动画')
            this.stop()
          }

          this.loadFromUrl(animUrl)
            .then(() => {
              console.log('[流程] 动画加载成功，准备播放:', animUrl)
              console.log('[流程] 当前是否为GIF:', !!this.currentGifOverlay)
              console.log('[流程] 解析的GIF时长:', this.currentGifDuration)

              // 优先从节点属性获取播放时长，没有再使用解析的GIF时长
              let playDuration = this.currentGifDuration // 默认使用解析的时长
              if (node.data && node.data.duration && node.data.duration > 0) {
                playDuration = node.data.duration
                console.log('[流程] 使用节点设置的播放时长:', playDuration + 'ms')
              } else {
                console.log('[流程] 使用解析的GIF播放时长:', playDuration + 'ms')
              }

              // 临时覆盖GIF时长用于此次播放
              const originalDuration = this.currentGifDuration
              this.currentGifDuration = playDuration

              // 清空之前的回调和定时器，防止重复触发
              this.onEnded(null)
              if (this.gifEndTimer) {
                clearTimeout(this.gifEndTimer)
                this.gifEndTimer = null
              }

              // 设置一次性回调，防止多次触发
              const next = () => {
                console.log('[流程] 动画播放结束，进入下一个节点')
                // 恢复原始GIF时长
                this.currentGifDuration = originalDuration

                // 触发外部结束事件（给组件用）
                if (this.flowOptions && this.flowOptions.onAnimationEnded) {
                  this.flowOptions.onAnimationEnded()
                }

                this.onEnded(null) // 清空回调
                this._playNextFlowNode()
              }
              this.onEnded(next)
              console.log('[流程] 已设置结束回调')

              this.play()
              console.log('[流程] 已调用play()')
              console.log('[流程] 当前flowIsPlaying状态:', this.flowIsPlaying)
            })
            .catch((err) => {
              console.error('[流程] 动画加载失败，跳过，err:', err)
              // 加载失败，直接进入下一个节点
              this.flowTimeout = setTimeout(() => this._playNextFlowNode(), 1000)
            })
        } else {
          console.warn('[流程] action节点无动画ID，直接进入下一个节点')
          this.flowTimeout = setTimeout(() => this._playNextFlowNode(), 500)
        }
        break
      }
      case 'delay': {
        // 延时节点
        const ms = node.data && node.data.ms ? node.data.ms : 1000
        this.flowTimeout = setTimeout(() => this._playNextFlowNode(), ms)
        break
      }
      case 'loop': {
        // 循环节点
        const loopCount = node.data && node.data.count ? node.data.count : 1
        const loopKey = node.id
        if (!this.flowLoopState[loopKey]) this.flowLoopState[loopKey] = 0
        const currentCount = this.flowLoopState[loopKey]
        // 查找loop-body和loop-exit
        const outgoingEdges = edges.filter(e => e.source === nodeId)
        const loopBodyEdge = outgoingEdges.find(e => e.sourceHandle === 'loop-body')
        const loopExitEdge = outgoingEdges.find(e => e.sourceHandle === 'loop-exit')
        if (currentCount < loopCount - 1 && loopBodyEdge) {
          this.flowLoopState[loopKey]++
          this.flowCurrentNodeId = loopBodyEdge.target
          this.flowTimeout = setTimeout(() => this._playFlowNode(loopBodyEdge.target), 300)
        } else if (loopExitEdge) {
          this.flowLoopState[loopKey] = 0
          this.flowCurrentNodeId = loopExitEdge.target
          this.flowTimeout = setTimeout(() => this._playFlowNode(loopExitEdge.target), 300)
        } else {
          // 没有出口，直接下一个
          this.flowTimeout = setTimeout(() => this._playNextFlowNode(), 300)
        }
        break
      }
      case 'random': {
        // 随机节点
        const outgoingEdges = edges.filter(e => e.source === nodeId)
        if (outgoingEdges.length > 0) {
          const randomIndex = Math.floor(Math.random() * outgoingEdges.length)
          const nextId = outgoingEdges[randomIndex].target
          this.flowCurrentNodeId = nextId
          this.flowTimeout = setTimeout(() => this._playFlowNode(nextId), 300)
        } else {
          this._endFlowSimulation()
        }
        break
      }
      case 'end': {
        // 结束节点
        this._endFlowSimulation()
        break
      }
      default: {
        // 其他类型，直接下一个
        this.flowTimeout = setTimeout(() => this._playNextFlowNode(), 300)
        break
      }
    }
  }

  /**
     * 内部方法：查找下一个流程节点
     */
  _findNextFlowNode (nodeId) {
    const edges = this.flowData.edges
    const nodes = this.flowData.nodes
    const outgoingEdges = edges.filter(e => e.source === nodeId)
    if (outgoingEdges.length === 0) return null
    const node = nodes.find(n => n.id === nodeId)
    if (node && node.type === 'random') {
      const randomIndex = Math.floor(Math.random() * outgoingEdges.length)
      return outgoingEdges[randomIndex].target
    }
    // 默认返回第一条出边
    return outgoingEdges[0].target
  }

  /**
     * 内部方法：结束流程模拟
     */
  _endFlowSimulation () {
    this.flowIsPlaying = false
    if (this.flowTimeout) {
      clearTimeout(this.flowTimeout)
      this.flowTimeout = null
    }
    if (this.flowOptions && this.flowOptions.onStatusChange) {
      this.flowOptions.onStatusChange('ended')
    }
    this.flowCurrentNodeId = null
    this.flowLoopState = {}
  }
}

// 导出为ES模块
export default AnimationPlayer
